// src/firebase/authMethods.js
import { GoogleAuthProvider, signInWithPopup, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendEmailVerification, sendPasswordResetEmail, signOut, setPersistence, browserLocalPersistence, updateProfile } from "firebase/auth";

import { auth } from "./firebaseConfig"; // Import Firebase auth instance


export const saveMarketingPreference = async (user, marketingOptIn, registration = false) => {
  const token = await user.getIdToken();
  const response = await fetch(
    registration ? '/api/marketing-preference/registration' : '/api/marketing-preference',
    {
      method: registration ? 'POST' : 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ marketingOptIn }),
    }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Unable to save marketing preference.');
  return data;
};

// Sign Up with Email and Password
export const signUpWithEmail = async (email, password, username, marketingOptIn) => {
  const normalizedUsername = String(username || '').trim();
  if (normalizedUsername.length < 3 || normalizedUsername.length > 20) {
    throw new Error('Username must be between 3 and 20 characters.');
  }

  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const user = result.user;
    // Keep the requested public username in Firebase until the verified user
    // signs in and the backend creates the corresponding MySQL profile.
    await updateProfile(user, { displayName: normalizedUsername });
    await sendEmailVerification(user, {
      url: `${window.location.origin}/?emailVerified=true`,
    });
    let preferenceSaved = true;
    try {
      await saveMarketingPreference(user, marketingOptIn, true);
    } catch (preferenceError) {
      // Verification is more important than optional marketing consent. Keep
      // signup successful and let the user set it later in Account Settings.
      preferenceSaved = false;
      console.error('Unable to save signup marketing preference:', preferenceError);
    }
    await signOut(auth);
    // console.log("Email Signed Up User:", user);
    return { user, preferenceSaved };
  } catch (error) {
    // A Firebase account may already have been created before a mail-delivery
    // error. Leave it recoverable through the resend flow, but do not keep it
    // authenticated locally.
    if (auth.currentUser) await signOut(auth);
    console.error("Error during Email Sign-up:", error.message);
    throw error;
  }
};
// Sign In with Email and Password
export const signInWithEmail = async (email, password) => {
  try {
    await setPersistence(auth, browserLocalPersistence)

    const result = await signInWithEmailAndPassword(auth, email, password);
    const user = result.user;
    // Reload before checking so a verification completed in another browser
    // tab is recognized immediately on the next login attempt.
    await user.reload();
    if (!user.emailVerified) {
      await signOut(auth);
      const error = new Error("Verify your email address before logging in.");
      error.code = "auth/email-not-verified";
      throw error;
    }
    await user.getIdToken(true);
    // console.log("Email Signed in User:", user);
    return user;
  } catch (error) {
    console.error("Error during Email Sign-in:", error.message);
    throw error;
  }
};

// Explicit resend action avoids sending a new email on every failed login.
export const resendVerificationEmail = async (email, password) => {
  const result = await signInWithEmailAndPassword(auth, email, password);
  const user = result.user;
  await user.reload();

  if (user.emailVerified) {
    await signOut(auth);
    return { alreadyVerified: true };
  }

  await sendEmailVerification(user, {
    url: `${window.location.origin}/?emailVerified=true`,
  });
  await signOut(auth);
  return { alreadyVerified: false };
};

// Sign In with Google
export const signInWithGoogle = async () => {

  const provider = new GoogleAuthProvider();
  try {
    // Open the popup immediately from the user's click. Awaiting another
    // operation first causes browsers to treat it as an unsolicited popup.
    // Firebase web authentication already uses local persistence by default.
    const result = await signInWithPopup(auth, provider);
    const user = result.user;
    // console.log("Google Signed in User:", user);
    return user;
  } catch (error) {
    if (error.code === "auth/popup-closed-by-user") {
      // console.log("User closed the popup. Signup process stopped.");
      return "closed"; // Don't show an error to the user, just exit
    }
    console.error("Firebase Authentication Error:", error);
    return "error"
    //throw error;
  }
};

// Reset Password
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    // console.log("Password Reset Email Sent!");
  } catch (error) {
    console.error("Error during password reset:", error.message);
    throw error;
  }
};

export const logOut = async () => {
  return signOut(auth)
    .then(() => {
      // console.log("User signed out successfully");
    })
    .catch((error) => {
      console.error("Error signing out: ", error);
    });
};

// Function to get the currently signed-in user info
export const getCurrentUser = async () => {
  const user = auth.currentUser;

  if (user) {
    // User is signed in, return the user object
    // console.log("User Info: ", user);
    return user;
  } else {
    // console.log("No user is signed in.");
    return null;
  }
};



